import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import './CampaignManagement.css';

function CampaignManagement() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    insuranceType: '',
    description: '',
    status: 'active',
    carriersFile: '',
    pricePerLead: ''
  });
  const [saving, setSaving] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/campaigns');
      setCampaigns(response.data.campaigns || []);
      setError(null);
    } catch (err) {
      console.error('Error loading campaigns:', err);
      setError('Failed to load campaigns. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCampaign = (campaignId) => {
    // Store selected campaign in localStorage
    localStorage.setItem('selectedCampaign', campaignId);
    
    // Redirect to existing admin dashboard
    navigate('/admin/dashboard');
  };

  const handleCreateCampaign = () => {
    setIsCreating(true);
    setFormData({
      name: '',
      insuranceType: '',
      description: '',
      status: 'active',
      carriersFile: '',
      pricePerLead: ''
    });
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      insuranceType: campaign.insuranceType,
      description: campaign.description || '',
      status: campaign.status,
      carriersFile: campaign.carriersFile || '',
      pricePerLead: campaign.pricePerLead || ''
    });
  };

  const handleCloseModal = () => {
    setEditingCampaign(null);
    setIsCreating(false);
    setFormData({
      name: '',
      insuranceType: '',
      description: '',
      status: 'active',
      carriersFile: '',
      pricePerLead: ''
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveCampaign = async () => {
    try {
      setSaving(true);
      await axios.put(`/api/admin/campaigns/${editingCampaign.campaignId}`, formData);
      
      // Refresh campaigns list
      await fetchCampaigns();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving campaign:', err);
      alert('Failed to save campaign. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCampaignSubmit = async () => {
    try {
      setSaving(true);
      await axios.post('/api/admin/campaigns', formData);
      
      // Refresh campaigns list
      await fetchCampaigns();
      handleCloseModal();
    } catch (err) {
      console.error('Error creating campaign:', err);
      alert('Failed to create campaign. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCampaign = (campaign) => {
    setDeletingCampaign(campaign);
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      await axios.delete(`/api/admin/campaigns/${deletingCampaign.campaignId}`);
      
      // Refresh campaigns list
      await fetchCampaigns();
      setDeletingCampaign(null);
    } catch (err) {
      console.error('Error deleting campaign:', err);
      alert(err.response?.data?.error || 'Failed to delete campaign. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeletingCampaign(null);
  };

  if (loading) {
    return (
      <div className="campaign-management">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading campaigns...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="campaign-management">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchCampaigns} className="btn-retry">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="campaign-management">
      <header className="campaign-header">
        <div className="header-content">
          <h1>Campaign Management</h1>
          <p className="subtitle">Select a campaign to manage</p>
        </div>
        <div style={{display: 'flex', gap: '12px'}}>
          <button onClick={handleCreateCampaign} className="btn-create">
            + Create Campaign
          </button>
          <button onClick={handleLogout} className="btn-create" style={{background: '#e74c3c'}}>
            Logout
          </button>
        </div>
      </header>

      {campaigns.length === 0 ? (
        <div className="empty-state">
          <p>No campaigns found</p>
          <button onClick={handleCreateCampaign} className="btn-create">
            Create Your First Campaign
          </button>
        </div>
      ) : (
        <div className="campaign-grid">
          {campaigns.map(campaign => (
            <div key={campaign.campaignId} className="campaign-card">
              <div className="campaign-header-card">
                <h2>{campaign.name}</h2>
                <span className={`status-badge ${campaign.status}`}>
                  {campaign.status}
                </span>
              </div>
              
              <p className="campaign-type">{campaign.insuranceType}</p>
              
              {campaign.pricePerLead && (
                <div style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  display: 'inline-block',
                  fontWeight: '600',
                  fontSize: '16px',
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                }}>
                  💵 ${parseFloat(campaign.pricePerLead).toFixed(2)} / lead
                </div>
              )}
              
              {campaign.description && (
                <p className="campaign-description">{campaign.description}</p>
              )}

              <div className="campaign-stats">
                <div className="stat-row">
                  <div className="stat">
                    <span className="stat-label">Agents</span>
                    <span className="stat-value">{campaign.stats?.activeAgents || 0}/{campaign.stats?.totalAgents || 0}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Today</span>
                    <span className="stat-value">{campaign.stats?.todayLeads || 0}</span>
                  </div>
                </div>
                <div className="stat-row">
                  <div className="stat">
                    <span className="stat-label">Total Opportunities</span>
                    <span className="stat-value">{(campaign.stats?.totalLeads || 0).toLocaleString()}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Remaining Opportunities</span>
                    <span 
                      className="stat-value"
                      style={{
                        color: (() => {
                          const totalPurchased = campaign.stats?.totalPurchased || 0;
                          const totalSent = campaign.stats?.totalSent || 0;
                          const remaining = totalPurchased - totalSent;
                          
                          if (remaining < 0) return '#dc2626';
                          if (remaining < totalPurchased * 0.2) return '#f59e0b';
                          return '#10b981';
                        })()
                      }}
                    >
                      {((campaign.stats?.totalPurchased || 0) - (campaign.stats?.totalSent || 0)).toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="stat-row">
                  <div className="stat" style={{ gridColumn: '1 / -1' }}>
                    <span className="stat-label">Capacity Usage</span>
                    <span className="stat-value-small"
                      style={{
                        color: (() => {
                          const totalPurchased = campaign.stats?.totalPurchased || 0;
                          const totalSent = campaign.stats?.totalSent || 0;
                          const percentage = totalPurchased > 0 ? ((totalPurchased - totalSent) / totalPurchased * 100).toFixed(1) : 0;
                          
                          if (percentage < 0) return '#dc2626';
                          if (percentage < 20) return '#f59e0b';
                          return '#6b7280';
                        })()
                      }}
                    >
                      {(campaign.stats?.totalSent || 0).toLocaleString()} / {(campaign.stats?.totalPurchased || 0).toLocaleString()} sent ({((campaign.stats?.totalPurchased || 0) > 0 ? (((campaign.stats?.totalPurchased || 0) - (campaign.stats?.totalSent || 0)) / (campaign.stats?.totalPurchased || 0) * 100).toFixed(1) : 0)}% left)
                    </span>
                  </div>
                </div>
              </div>

              <div className="campaign-actions">
                <button 
                  onClick={() => handleSelectCampaign(campaign.campaignId)}
                  className="btn-select"
                  disabled={campaign.status !== 'active'}
                >
                  {campaign.status === 'active' ? 'Select' : 'Archived'}
                </button>
                <button 
                  onClick={() => handleEditCampaign(campaign)}
                  className="btn-edit"
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleDeleteCampaign(campaign)}
                  className="btn-delete"
                  title="Delete campaign"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Campaign Modal */}
      {editingCampaign && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Campaign</h2>
              <button onClick={handleCloseModal} className="btn-close">×</button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleSaveCampaign(); }}>
              <div className="form-group">
                <label>Campaign ID</label>
                <input
                  type="text"
                  value={editingCampaign.campaignId}
                  disabled
                />
              </div>

              <div className="form-group">
                <label>Campaign Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Insurance Type *</label>
                <input
                  type="text"
                  name="insuranceType"
                  value={formData.insuranceType}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  placeholder="Optional campaign description..."
                />
              </div>

              <div className="form-group">
                <label>Price Per Opportunity ($)</label>
                <input
                  type="number"
                  name="pricePerLead"
                  value={formData.pricePerLead}
                  onChange={handleFormChange}
                  placeholder="e.g., 25.00"
                  step="0.01"
                  min="0"
                />
                <small style={{color: '#7f8c8d', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                  The selling price per opportunity for this campaign
                </small>
              </div>

              <div className="form-group">
                <label>Status *</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                  required
                >
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-cancel"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Campaign Modal */}
      {isCreating && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Campaign</h2>
              <button onClick={handleCloseModal} className="btn-close">×</button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleCreateCampaignSubmit(); }}>
              <div className="form-group">
                <label>Campaign Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder="e.g., Final Expense"
                  required
                />
              </div>

              <div className="form-group">
                <label>Insurance Type *</label>
                <input
                  type="text"
                  name="insuranceType"
                  value={formData.insuranceType}
                  onChange={handleFormChange}
                  placeholder="e.g., Final Expense Insurance"
                  required
                />
              </div>

              <div className="form-group">
                <label>Carriers File *</label>
                <input
                  type="text"
                  name="carriersFile"
                  value={formData.carriersFile}
                  onChange={handleFormChange}
                  placeholder="e.g., final-expense-carriers.json"
                  required
                />
                <small style={{color: '#7f8c8d', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                  The JSON file name in the root directory containing carrier data
                </small>
              </div>

              <div className="form-group">
                <label>Price Per Opportunity ($)</label>
                <input
                  type="number"
                  name="pricePerLead"
                  value={formData.pricePerLead}
                  onChange={handleFormChange}
                  placeholder="e.g., 25.00"
                  step="0.01"
                  min="0"
                />
                <small style={{color: '#7f8c8d', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                  The selling price per opportunity for this campaign
                </small>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  placeholder="Optional campaign description..."
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-cancel"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={saving}
                >
                  {saving ? 'Creating...' : 'Create Campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingCampaign && (
        <div className="modal-overlay" onClick={handleCancelDelete}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Campaign</h2>
              <button onClick={handleCancelDelete} className="btn-close">×</button>
            </div>

            <div className="modal-body">
              <p style={{marginBottom: '16px', color: '#e74c3c', fontWeight: '500'}}>
                ⚠️ Warning: This action cannot be undone!
              </p>
              <p style={{marginBottom: '8px'}}>
                Are you sure you want to delete the campaign:
              </p>
              <p style={{fontWeight: 'bold', fontSize: '16px', marginBottom: '16px'}}>
                "{deletingCampaign.name}"?
              </p>
              <p style={{color: '#7f8c8d', fontSize: '14px'}}>
                This will permanently remove the campaign and all associated data.
              </p>
            </div>

            <div className="modal-actions">
              <button
                onClick={handleCancelDelete}
                className="btn-cancel"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="btn-delete"
                disabled={deleting}
                style={{
                  background: '#e74c3c',
                  color: 'white',
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.7 : 1
                }}
              >
                {deleting ? 'Deleting...' : 'Delete Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignManagement;
